import { isClerkAPIResponseError } from '@clerk/react/errors';
import { type Maybe, isUUID } from '@vrplatform/utils';
import type { Team } from './team.types';

const SESSION_TOKEN_TEMPLATE = 'Hasura';

type ResolveSessionTeamIdArgs = {
  userId: string | undefined;
  /**
   * The authoritative team id from the settled `/me` response:
   * `string` = resolved team, `null` = settled teamless,
   * `undefined` = `/me` has not settled for the requested team yet.
   */
  meTeamId: string | null | undefined;
  hasMeError: boolean;
};

type ResolveSessionTeamIdResult = {
  resolvedTeamId: string | null | undefined;
  isBootstrappingTeamId: boolean;
};

export const RECENT_TEAM_IDS_LIMIT = 5;

export const getRecentTeamIdsStorageKey = (userId: string) =>
  `${userId}_recent_tid`;

export const parseStoredRecentTeamIds = (value: string | null): string[] => {
  if (!value) return [];

  try {
    const parsed = JSON.parse(value);

    if (!Array.isArray(parsed)) return [];

    return parsed.filter((item): item is string => typeof item === 'string');
  } catch {
    return [];
  }
};

export const upsertRecentTeamId = ({
  teamId,
  recentTeamIds,
  limit = RECENT_TEAM_IDS_LIMIT,
}: {
  teamId: string | null | undefined;
  recentTeamIds: readonly string[];
  limit?: number;
}) => {
  if (!teamId) return [...recentTeamIds].slice(0, limit);

  return [teamId, ...recentTeamIds.filter((id) => id !== teamId)].slice(
    0,
    limit
  );
};

const isMissingActiveSessionError = (
  error: unknown,
  sessionId: string | undefined
) => {
  if (!sessionId || !isClerkAPIResponseError(error) || error.status !== 404)
    return false;

  const expectedMessage = `No session was found with id ${sessionId}`;
  const isResourceNotFound = error.errors.some(
    (clerkError) => clerkError.code === 'resource_not_found'
  );
  const isExpectedSession =
    error.message === expectedMessage ||
    error.errors.some(
      (clerkError) =>
        clerkError.message === expectedMessage ||
        clerkError.longMessage === expectedMessage
    );

  return isResourceNotFound && isExpectedSession;
};

/**
 * PRO-17358: every portal API provider shares one token provider. Ordinary
 * requests keep using the cached Clerk token, while a rejected credential
 * triggers a single forced refresh that bypasses the cache — parallel queries
 * seeing the same stale token wait on that one refresh instead of each asking
 * Clerk for a new one. PRO-17551/PRO-17552: if Clerk reports that this exact
 * active session no longer exists, clear the derived token and coalesce one
 * local session deactivation instead of propagating the same error per query.
 */
export const createSessionTokenProvider = ({
  getSessionToken,
  isSessionCurrent,
  onSessionNotFound,
  onToken,
  sessionId,
}: {
  getSessionToken: (options: {
    template: string;
    skipCache?: true;
  }) => Promise<Maybe<string>> | undefined;
  isSessionCurrent: () => boolean;
  onSessionNotFound: () => void | Promise<void>;
  onToken: (token: Maybe<string>) => void;
  sessionId: string | undefined;
}) => {
  let forcedRefresh: Promise<Maybe<string>> | null = null;
  let missingSessionRecovery: Promise<void> | null = null;

  const acquireToken = async (skipCache?: true) => {
    if (missingSessionRecovery) {
      await missingSessionRecovery;
      return undefined;
    }

    try {
      const token = await getSessionToken({
        template: SESSION_TOKEN_TEMPLATE,
        ...(skipCache ? { skipCache } : {}),
      });

      if (missingSessionRecovery) {
        await missingSessionRecovery;
        return undefined;
      }

      onToken(token);

      return token;
    } catch (error) {
      if (!isMissingActiveSessionError(error, sessionId)) throw error;

      if (!missingSessionRecovery) {
        missingSessionRecovery = Promise.resolve().then(async () => {
          if (!isSessionCurrent()) return;

          onToken(undefined);
          await onSessionNotFound();
        });
      }

      await missingSessionRecovery;
      return undefined;
    }
  };

  return {
    getToken: () => acquireToken(),
    refreshToken: () => {
      if (!forcedRefresh) {
        forcedRefresh = acquireToken(true).finally(() => {
          forcedRefresh = null;
        });
      }

      return forcedRefresh;
    },
  };
};

type SessionTokenClerk = {
  session:
    | {
        id: string;
        getToken: (options: {
          template: string;
          skipCache?: true;
        }) => Promise<Maybe<string>>;
      }
    | null
    | undefined;
  setActive: (params: { session: null }) => Promise<void>;
};

const sharedSessionTokenProviders = new WeakMap<
  SessionTokenClerk,
  {
    provider: ReturnType<typeof createSessionTokenProvider>;
    sessionId: string | undefined;
  }
>();

/**
 * PRO-18813/PRO-18814: the REST, Hasura, and tRPC clients authenticate with
 * the same Clerk session, so they share one token provider per Clerk instance
 * and session. A missing session is then recovered once for every client, and
 * Hasura/tRPC requests stop surfacing the Clerk error as a query failure.
 */
export const getSharedSessionTokenProvider = (
  clerk: SessionTokenClerk,
  onToken: (token: Maybe<string>) => void
) => {
  const session = clerk.session;
  const cached = sharedSessionTokenProviders.get(clerk);

  if (cached && cached.sessionId === session?.id) return cached.provider;

  const provider = createSessionTokenProvider({
    getSessionToken: (options) => session?.getToken(options),
    isSessionCurrent: () => clerk.session?.id === session?.id,
    onSessionNotFound: () => clerk.setActive({ session: null }),
    onToken,
    sessionId: session?.id,
  });
  sharedSessionTokenProviders.set(clerk, { provider, sessionId: session?.id });

  return provider;
};

const getImpersonationActorClaim = (actor: unknown, key: 'cti' | 'sti') => {
  if (!actor || typeof actor !== 'object') return undefined;

  const value = (actor as Record<string, unknown>)[key];

  return typeof value === 'string' && isUUID(value) ? value : undefined;
};

export const getImpersonationActorTeamId = (actor: unknown) =>
  getImpersonationActorClaim(actor, 'sti');

export const getImpersonationActorContactId = (actor: unknown) =>
  getImpersonationActorClaim(actor, 'cti');

export const resolveSessionRequestedTeamId = ({
  actorTeamId,
  hasResolvedActorTeam,
  stiTeamId,
  storedTeamId,
}: {
  actorTeamId: string | undefined;
  hasResolvedActorTeam: boolean;
  stiTeamId: string | undefined;
  storedTeamId: string | null | undefined;
}) =>
  actorTeamId && !hasResolvedActorTeam
    ? actorTeamId
    : (stiTeamId ?? storedTeamId ?? actorTeamId);

/**
 * Only the settled `/me` response resolves the active team: the returned
 * `team.id` is authoritative. While unsettled the team id stays `undefined`,
 * which keeps every other team-scoped query gated.
 */
export const resolveSessionTeamId = ({
  userId,
  meTeamId,
  hasMeError,
}: ResolveSessionTeamIdArgs): ResolveSessionTeamIdResult => {
  const resolvedTeamId =
    meTeamId !== undefined ? meTeamId : hasMeError ? null : undefined;

  const isBootstrappingTeamId = !!userId && resolvedTeamId === undefined;

  return {
    resolvedTeamId,
    isBootstrappingTeamId,
  };
};

export type MeTeamReconciliation = {
  /** value to persist as the stored team id (`null` clears it) */
  persistTeamId: string | null;
  clearSti: boolean;
  /** the URL `sti` resolved to another team */
  warnStiMismatch: boolean;
  /** the impersonation actor's team resolved to another team */
  exitImpersonation: boolean;
};

/**
 * Reconciles the settled `/me` team against the requested team sources.
 * The API response is authoritative; requested ids are preferences.
 */
export const reconcileMeTeam = ({
  meTeamId,
  stiTeamId,
  actorTeamId,
}: {
  meTeamId: string | null;
  stiTeamId: string | undefined;
  actorTeamId: string | undefined;
}): MeTeamReconciliation => {
  const persistTeamId = meTeamId ?? null;

  if (actorTeamId) {
    return {
      persistTeamId,
      clearSti: false,
      warnStiMismatch: false,
      exitImpersonation: meTeamId !== actorTeamId,
    };
  }

  if (stiTeamId) {
    return {
      persistTeamId,
      clearSti: true,
      warnStiMismatch: meTeamId !== stiTeamId,
      exitImpersonation: false,
    };
  }

  return {
    persistTeamId,
    clearSti: false,
    warnStiMismatch: false,
    exitImpersonation: false,
  };
};

/**
 * Builds the `/me` query key for another requested team so a successful
 * fallback/default response can seed the cache for the resolved team without
 * issuing a second bootstrap request. The variables object is located inside
 * the key by its `requestedTeamId` property.
 */
export const createSeededMeQueryKey = (
  queryKey: readonly unknown[],
  requestedTeamId: string | null
): unknown[] =>
  queryKey.map((part) =>
    part &&
    typeof part === 'object' &&
    !Array.isArray(part) &&
    'requestedTeamId' in part
      ? { ...part, requestedTeamId }
      : part
  );

/**
 * Portal team routing (PRO-18799). On page loads the portal router serves
 * `ui-vrintegrations` for non-GL property-manager teams and `ui` for every
 * other team or unresolved team. It names the app it served in a meta tag. The
 * app keeps the router's team preference cookie current and reloads when the
 * resolved team belongs to the other app.
 */
export type PortalApp = 'ui' | 'vri';

export const PORTAL_APP_META_NAME = 'vrp-portal-app';
export const PORTAL_TEAM_COOKIE_NAME = 'vrp-portal-team';
export const PORTAL_RELOAD_STORAGE_KEY = 'vrp-portal-reload';

export const resolvePortalApp = (
  team: Pick<Team, 'type' | 'isGL'> | null
): PortalApp => (team?.type === 'propertyManager' && !team.isGL ? 'vri' : 'ui');

/** `null` when the page was not served through team routing. */
export const readRoutedPortalApp = (
  doc: Pick<Document, 'querySelector'>
): PortalApp | null => {
  const content = doc
    .querySelector(`meta[name="${PORTAL_APP_META_NAME}"]`)
    ?.getAttribute('content');

  if (content == null) return null;
  if (content === 'ui' || content === 'vri') return content;

  throw new Error(`Invalid ${PORTAL_APP_META_NAME} meta value: ${content}`);
};

export const serializePortalTeamCookie = (teamId: string | null) =>
  teamId
    ? `${PORTAL_TEAM_COOKIE_NAME}=${encodeURIComponent(teamId)}; Path=/; Max-Age=31536000; SameSite=Lax; Secure`
    : `${PORTAL_TEAM_COOKIE_NAME}=; Path=/; Max-Age=0; SameSite=Lax; Secure`;

export type PortalRoutingDecision =
  | { action: 'none' }
  | {
      action: 'reload' | 'start';
      expectedApp: PortalApp;
      reloadKey: string;
      reloadPath: string;
    }
  | {
      action: 'mismatch';
      expectedApp: PortalApp;
      reloadKey: string;
    };

/**
 * Reloads at most once per team and app, so a router that keeps serving the
 * other app surfaces as a mismatch instead of a reload loop. The router always
 * serves ui on the sign-in landing pages, so a session that signs in there
 * starts the other app at `/` instead of reloading the landing page.
 */
export const resolvePortalRouting = ({
  routedApp,
  team,
  lastReloadKey,
  pathname,
  search = '',
}: {
  routedApp: PortalApp;
  team: Pick<Team, 'id' | 'type' | 'isGL'> | null;
  lastReloadKey: string | null;
  pathname: string;
  search?: string;
}): PortalRoutingDecision => {
  const expectedApp = resolvePortalApp(team);
  if (expectedApp === routedApp) return { action: 'none' };

  const reloadKey = `${team?.id ?? 'teamless'}:${expectedApp}`;
  const action = ['/sso-callback', '/token-verify'].includes(pathname)
    ? 'start'
    : lastReloadKey === reloadKey
      ? 'mismatch'
      : 'reload';
  if (action === 'mismatch') return { action, expectedApp, reloadKey };

  // Build the document destination without changing this app's query state.
  const params = new URLSearchParams(action === 'start' ? '' : search);
  if (team) params.set('sti', team.id);
  return {
    action,
    expectedApp,
    reloadKey,
    reloadPath: `${action === 'start' ? '/' : pathname}${params.size ? `?${params}` : ''}`,
  };
};

/**
 * PRO-18864: the app this page loads for a cross-app team switch, while the
 * routing check is still pending. Only a page the router served as the app
 * the reload asked for counts; a page that is about to report a mismatch
 * does not.
 */
export const readPortalSwitchLanding = (
  doc: Pick<Document, 'querySelector'>,
  storage: Pick<Storage, 'getItem'>
): PortalApp | null => {
  const reloadKey = storage.getItem(PORTAL_RELOAD_STORAGE_KEY);
  if (!reloadKey) return null;

  const routedApp = readRoutedPortalApp(doc);
  return routedApp && reloadKey.endsWith(`:${routedApp}`) ? routedApp : null;
};

export const getPortalSwitchMessage = (app: PortalApp) =>
  `Switching to ${app === 'vri' ? 'VRIntegrations' : 'VRPlatform'}`;
